import React, { useState, useEffect } from "react";
import api from "../../utils/axiosInstance";
import { Card, CardBody, Badge } from "../../components/ui";
import { FaPlus, FaTrash, FaKey, FaCopy, FaCheck, FaBook, FaExternalLinkAlt } from "react-icons/fa";
import { useModal } from "../../context/ModalContext";
import { useWorkspace } from "../../context/WorkspaceContext";

export default function ApiKeysTab() {
  const modal = useModal();
  const { activeWorkspace } = useWorkspace() || {};
  const [keys, setKeys] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [revealedKey, setRevealedKey] = useState(null);
  const [revealedCopied, setRevealedCopied] = useState(false);
  const [workspaceCopied, setWorkspaceCopied] = useState(false);

  const workspaceId = activeWorkspace?.clientId || activeWorkspace?.id || "";

  const fetchKeys = () => {
    api.get("/api/settings/api-keys").then(res => setKeys(res.data))
      .catch(err => console.error(err));
  };

  const generateKey = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/api/settings/api-keys/generate", { name: name.trim() });
      setName("");
      setShowCreate(false);
      if (res?.data?.key) {
        setRevealedKey({ key: res.data.key, name: res.data.name || "Unnamed Key" });
        setRevealedCopied(false);
      }
      fetchKeys();
    } catch (err) {
      console.error(err);
      modal.alert("Error generating API key", "error");
    }
  };

  const deleteKey = async (id) => {
    if (!(await modal.confirm("Delete this API key? Any integrations using it will stop working.", { danger: true, confirmText: "Delete" }))) return;
    try {
      await api.delete(`/api/settings/api-keys/${id}`);
      fetchKeys();
    } catch (err) {
      console.error(err);
      modal.alert("Error deleting API key", "error");
    }
  };

  const copyRevealedKey = () => {
    if (!revealedKey?.key) return;
    navigator.clipboard.writeText(revealedKey.key);
    setRevealedCopied(true);
    setTimeout(() => setRevealedCopied(false), 2000);
  };

  const copyWorkspaceId = () => {
    if (!workspaceId) return;
    navigator.clipboard.writeText(workspaceId);
    setWorkspaceCopied(true);
    setTimeout(() => setWorkspaceCopied(false), 2000);
  };

  useEffect(() => {
    fetchKeys();
  }, []);

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-semibold text-ink-primary">API Keys</h2>
        <button className="btn-primary btn-sm flex items-center gap-1.5" onClick={() => setShowCreate(true)}>
          <FaPlus size={11} /> Generate New Key
        </button>
      </div>

      {/* Create Key Form */}
      {showCreate && (
        <Card className="mb-5">
          <CardBody>
            <h3 className="text-sm font-semibold text-ink-primary mb-3 flex items-center gap-2">
              <FaKey size={12} className="text-brand" /> New API Key
            </h3>
            <form onSubmit={generateKey} className="flex items-end gap-3">
              <div className="input-group flex-1">
                <label className="input-label">Key Name</label>
                <input
                  className="input"
                  placeholder="e.g. Production Server, n8n Integration, Testing"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                />
                <p className="input-hint">Give your key a name so you can identify it later</p>
              </div>
              <div className="flex gap-2 pb-5">
                <button type="submit" className="btn-primary btn-sm flex items-center gap-1.5">
                  <FaCheck size={11} /> Create
                </button>
                <button type="button" className="btn-secondary btn-sm" onClick={() => { setShowCreate(false); setName(""); }}>
                  Cancel
                </button>
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      <div className="mb-5 grid grid-cols-1 xl:grid-cols-2 gap-3">
        <div className="p-4 rounded-xl border border-brand/20 bg-brand-50/30 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-brand/10 flex items-center justify-center flex-shrink-0">
              <FaBook size={16} className="text-brand" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-ink-primary">API Documentation</p>
              <p className="text-xs text-ink-muted">Integrate with Zapier, Pabbly, n8n, or your own apps</p>
            </div>
          </div>
          <a
            href="/integration-and-api"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary btn-sm flex items-center gap-1.5 flex-shrink-0"
          >
            <FaExternalLinkAlt size={10} /> View Docs
          </a>
        </div>

        <div className="p-4 rounded-xl border border-surface-border bg-surface-bg flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-ink-primary">Workspace Client ID</p>
            <p className="text-xs text-ink-muted mb-2">
              Send this as <code className="font-mono">X-Workspace-Id</code> with your API key.
            </p>
            <code className="block text-xs font-mono text-ink-secondary truncate">
              {workspaceId || "Workspace loading..."}
            </code>
          </div>
          <button
            type="button"
            className="btn-secondary btn-sm btn-icon flex-shrink-0"
            onClick={copyWorkspaceId}
            disabled={!workspaceId}
            title="Copy workspace ID"
          >
            {workspaceCopied ? <FaCheck size={11} className="text-green-500" /> : <FaCopy size={11} />}
          </button>
        </div>
      </div>

      {revealedKey && (
        <Card className="mb-5 border-amber-400/40 bg-amber-50/40">
          <CardBody>
            <div className="flex items-start gap-3">
              <FaKey className="text-amber-600 mt-1 flex-shrink-0" size={14} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-ink-primary mb-1">
                  New API key for &ldquo;{revealedKey.name}&rdquo;
                </p>
                <p className="text-xs text-ink-muted mb-3">
                  Save this key now. It will not be shown again. If you lose it, delete this key and generate a new one.
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs font-mono p-2 rounded bg-surface-bg border border-surface-border truncate">
                    {revealedKey.key}
                  </code>
                  <button
                    className="btn-primary btn-sm btn-icon flex-shrink-0"
                    onClick={copyRevealedKey}
                    title="Copy key"
                  >
                    {revealedCopied ? <FaCheck size={11} className="text-green-500" /> : <FaCopy size={11} />}
                  </button>
                  <button
                    className="btn-secondary btn-sm flex-shrink-0"
                    onClick={() => { setRevealedKey(null); setRevealedCopied(false); }}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      <div className="space-y-2">
        {keys.length === 0 && (
          <p className="text-sm text-ink-muted py-4">No API keys generated yet.</p>
        )}
        {keys.map(k => (
          <div key={k._id} className="flex items-center gap-3 p-3 rounded-lg border border-surface-border bg-surface-bg">
            <FaKey size={12} className="text-ink-faint flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-sm font-medium text-ink-primary truncate">
                  {k.name || "Unnamed Key"}
                </span>
                {!k.name && <Badge variant="neutral" className="text-[10px]">No name</Badge>}
              </div>
              <code className="text-xs text-ink-muted font-mono truncate block">
                {k.keyPrefix
                  ? `••••••••${k.keyPrefix}`
                  : "Key not retrievable. Delete and regenerate."}
              </code>
            </div>
            <span className="text-[10px] text-ink-faint flex-shrink-0 hidden sm:block">
              {new Date(k.createdAt).toLocaleDateString()}
            </span>
            <button
              className="btn-secondary btn-sm btn-icon flex-shrink-0 text-red-500 hover:!bg-red-950/20"
              onClick={() => deleteKey(k._id)}
              title="Delete key"
            >
              <FaTrash size={11} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
